import React from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { formatDate } from '../../dashboard/components/dateUtils';
import { parseRawFormData } from '../../dashboard/components/formUtils';
import { HandlersTrResponse } from '../../app/athenaApiGen';
import {
  getWorkflowProgress,
  WorkflowProgressValue,
} from '../../utils/workflow';

// Extended type to handle fields from rawFormData and alternate field names
type TRApiItem = HandlersTrResponse & {
  RawFormData?: string;
  travelDate?: string;
  workflowState?: string;
  workflowProgress?: WorkflowProgressValue;
  travelerName?: string;
};

export interface TRRowData {
  id: string;
  trNumber: string;
  fromDate: string | null;
  toDate: string | null;
  projectName: string;
  travelerName: string;
  from: string;
  to: string;
  tarId: string | null;
  totalCostOfTrip: number | null;
  workflowState: string | null;
  workflowProgress: WorkflowProgressValue;
  workflowId: string | null;
}

export const createTRColumns = (): GridColDef<TRRowData>[] => [
  { field: 'trNumber', headerName: 'TR #', minWidth: 140, flex: 0.6 },
  {
    field: 'fromDate',
    headerName: 'Departure Date',
    minWidth: 150,
    renderCell: (params) => formatDate(params?.value),
    sortable: false,
    flex: 0.6,
  },
  {
    field: 'toDate',
    headerName: 'Arrival Date',
    minWidth: 150,
    renderCell: (params) => formatDate(params?.value),
    sortable: false,
    flex: 0.6,
  },
  { field: 'projectName', headerName: 'Project', minWidth: 220, flex: 1 },
  { field: 'travelerName', headerName: 'Traveler', minWidth: 180, flex: 0.9 },
  { field: 'from', headerName: 'From', minWidth: 140, flex: 0.7 },
  { field: 'to', headerName: 'To', minWidth: 140, flex: 0.7 },
  {
    field: 'totalCostOfTrip',
    headerName: 'Total Cost',
    minWidth: 130,
    flex: 0.6,
    sortable: false,
    renderCell: (params) => params.value != null ? `$${Number(params.value).toFixed(2)}` : '--',
  },
  {
    field: 'workflowState',
    headerName: 'Status',
    minWidth: 200,
    flex: 0.9,
    sortable: false,
    valueGetter: (value: string) => value ?? '-',
  },
];

const FALLBACK_TEXT = '--';

/**
 * Returns the first non-empty string from a list of candidate values.
 */
const pickFirstString = (
  ...values: Array<string | null | undefined>
): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim().length > 0) {
      return value;
    }
  }
  return undefined;
};

/**
 * Safely extracts a location identifier from raw API or form-backed values.
 */
export const readLocationId = (value: unknown): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'object') {
    const maybeRecord = value as Record<string, any>;
    return (
      maybeRecord.locationId ??
      maybeRecord.id ??
      maybeRecord.value ??
      maybeRecord.key ??
      maybeRecord?.location?.locationId ??
      undefined
    );
  }
  return undefined;
};

/**
 * Safely extracts a displayable location label from raw API or form data.
 */
export const readLocationName = (value: unknown): string | undefined => {
  if (!value) return undefined;
  if (typeof value === 'string' && value.trim()) return value;
  if (typeof value === 'object') {
    const maybeRecord = value as Record<string, any>;
    return (
      maybeRecord.name ??
      maybeRecord.label ??
      maybeRecord.description ??
      maybeRecord.locationName ??
      maybeRecord.locationCode ??
      maybeRecord?.location?.name ??
      maybeRecord?.location?.label ??
      undefined
    );
  }
  return undefined;
};

type TRLookupMaps = {
  locationMap?: Record<string, string>;
  travelerMap?: Record<string, string>;
};

/**
 * Normalizes Trip Report API payloads into grid-ready rows with lookup fallbacks.
 */
export const mapTRApiDataToRows = (
  items: TRApiItem[],
  lookups: TRLookupMaps = {},
): TRRowData[] => {
  const { locationMap = {}, travelerMap = {} } = lookups;

  return items.map((it) => {
    const raw = parseRawFormData(it.rawFormData || it.RawFormData);
    const fromDate =
      pickFirstString(
        it.fromDate,
        raw.fromDate,
        raw?.travelDates?.start,
        raw?.travelDates?.from,
        raw?.travelDates?.departure,
      ) || null;
    const toDate =
      pickFirstString(
        it.toDate,
        raw.toDate,
        raw?.travelDates?.end,
        raw?.travelDates?.to,
        raw?.travelDates?.return,
      ) || null;

    const workflowState =
      pickFirstString(
        it.workflowState as string | undefined,
        raw.workflowState,
        raw?.workflow?.state,
        raw?.workflowStateLabel,
      ) || null;
    const workflowProgress =
      (it.workflowProgress as WorkflowProgressValue | undefined) ??
      (raw.workflowProgress as WorkflowProgressValue | undefined) ??
      getWorkflowProgress(workflowState ?? undefined);

    const travelerId =
      pickFirstString(
        it.travelerId,
        raw.contactId,
        raw.travelerId,
        raw?.traveler?.contactId,
        raw?.traveler?.id,
      ) || undefined;
    const travelerName =
      pickFirstString(
        it.travelerName,
        raw.travelerName,
        raw?.traveler?.name,
        raw?.traveler?.userName,
        raw?.traveler?.label,
        raw?.traveler?.fullName,
        raw?.traveler?.displayName,
        travelerId ? travelerMap[travelerId] : undefined,
      ) ?? FALLBACK_TEXT;

    const fromLocationId =
      readLocationId(it.fromLocationOfTravel) ??
      readLocationId(raw.fromLocationOfTravel) ??
      readLocationId(raw?.fromLocation) ??
      undefined;
    const fromLocation =
      pickFirstString(
        fromLocationId ? locationMap[fromLocationId] : undefined,
        raw.fromLocationName,
        raw?.fromLocation?.name,
        raw?.fromLocation?.label,
        raw?.fromLocation?.description,
        readLocationName(it.fromLocationOfTravel),
        readLocationName(raw.fromLocationOfTravel),
        readLocationName(raw?.fromLocation),
        fromLocationId,
      ) ?? FALLBACK_TEXT;

    const toLocationId =
      readLocationId(it.toLocationOfTravel) ??
      readLocationId(raw.toLocationOfTravel) ??
      readLocationId(raw?.toLocation) ??
      undefined;
    const toLocation =
      pickFirstString(
        toLocationId ? locationMap[toLocationId] : undefined,
        raw.toLocationName,
        raw?.toLocation?.name,
        raw?.toLocation?.label,
        raw?.toLocation?.description,
        readLocationName(it.toLocationOfTravel),
        readLocationName(raw.toLocationOfTravel),
        readLocationName(raw?.toLocation),
        toLocationId,
      ) ?? FALLBACK_TEXT;

    const totalCostOfTrip = 
      it.totalCostOfTrip ?? 
      raw.totalCostOfTrip ?? 
      raw.totalCost ?? 
      null;

    return {
      id: it.trId ?? '',
      trNumber: pickFirstString(it.trNumber, raw.trNumber) ?? FALLBACK_TEXT,
      fromDate,
      toDate,
      projectName: pickFirstString(it.projectName, raw.projectName) ?? FALLBACK_TEXT,
      travelerName,
      from: fromLocation,
      to: toLocation,
      tarId: it.tarId || raw.tarId || null,
      totalCostOfTrip: totalCostOfTrip != null ? Number(totalCostOfTrip) : null,
      workflowState,
      workflowProgress,
      workflowId: it.workflowId ?? null,
    };
  });
};
